import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import WizardSteps from "../WizardSteps/WizardSteps";
import FormField from "../FormField/FormField";
import ChoiceCard, { ACCENT_TEAL } from "../ChoiceCard/ChoiceCard";
import ClinicField from "../ClinicField/ClinicField";
import {
  AddressFields,
  CutoverDateField,
} from "../PacServerJobsForm/PacServerJobsForm";

import {
  PacServerFormValues,
  buildDicomAddress,
  buildPacServerJobs,
  createPacServerFormValues,
  findDicomPacServerDevice,
  findPacServerJob,
  isoFromDateInputValue,
  pacServerFormValuesFromServer,
  todayDateInputValue,
} from "src/utils/emr/pacServerHelper";

import { useCreateEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useCreateEMRPacServer";
import { useUpdateEMRPacServer } from "src/services/emr/hooks/emr-pac-servers/useUpdateEMRPacServer";
import { useCreateEMRHubConnection } from "src/services/emr/hooks/emr-connections/useCreateEMRHubConnection";

import {
  EMRCatalogDeviceType,
  EMRHubType,
  EMRPacServerType,
} from "src/services/emr/types";

import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

type Destination = "PacServer" | "ExportFolder";
export type ReportFormat = "Pdf" | "Dicom";

const NEW_PAC_SERVER = "";

type AddReportDestinationModalType = {
  open: boolean;
  clinicId: string | undefined;
  hubId: string | undefined;
  hub: EMRHubType | undefined;
  devices: Array<EMRCatalogDeviceType>;
  pacServers: Array<EMRPacServerType> | undefined;
  listenPort: string;
  folderPaths: Array<string> | undefined;
  onClose: () => void;
};

const withReportsOn = (values: PacServerFormValues): PacServerFormValues => ({
  ...values,
  reports: { ...values.reports, enabled: true },
});

const AddReportDestinationModal = ({
  open,
  clinicId,
  hubId,
  hub,
  devices,
  pacServers,
  listenPort,
  folderPaths,
  onClose,
}: AddReportDestinationModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText, onSurface } = getSurfaceTokens(theme);

  const [step, setStep] = useState(0);
  const [destination, setDestination] = useState<Destination>("PacServer");
  const [pacServerId, setPacServerId] = useState(NEW_PAC_SERVER);
  const [values, setValues] = useState<PacServerFormValues | null>(null);
  const [folderName, setFolderName] = useState("");
  const [folderPath, setFolderPath] = useState("");
  const [formats, setFormats] = useState<Array<ReportFormat>>(["Pdf"]);
  const [folderExportFrom, setFolderExportFrom] = useState(todayDateInputValue);

  const dicomDevice = useMemo(
    () => findDicomPacServerDevice(devices),
    [devices]
  );

  const reusablePacServers = useMemo(
    () =>
      (pacServers ?? []).filter(
        (pacServer) =>
          pacServer.clinicId === clinicId &&
          !findPacServerJob(pacServer, ["DicomStore"])?.enabled
      ),
    [pacServers, clinicId]
  );
  const existingPacServer = reusablePacServers.find(
    (pacServer) => pacServer.pacServerId === pacServerId
  );
  const device = existingPacServer
    ? devices.find((item) => item.key === existingPacServer.deviceKey)
    : dicomDevice;

  const { mutate: createPacServer, isPending: isCreatingPacServer } =
    useCreateEMRPacServer(clinicId, hubId);
  const { mutate: updatePacServer, isPending: isUpdatingPacServer } =
    useUpdateEMRPacServer(clinicId, hubId);
  const { mutate: createConnection, isPending: isCreatingConnection } =
    useCreateEMRHubConnection(clinicId, hubId);

  const jobs = useMemo(
    () => (values ? buildPacServerJobs(values, device) : null),
    [values, device]
  );

  const trimmedPath = folderPath.trim();
  const isDuplicatePath = Boolean(folderPaths?.includes(trimmedPath));
  const folderExportFromIso = isoFromDateInputValue(folderExportFrom);

  const handleClose = useCallback(() => {
    setStep(0);
    setDestination("PacServer");
    setPacServerId(NEW_PAC_SERVER);
    setValues(null);
    setFolderName("");
    setFolderPath("");
    setFormats(["Pdf"]);
    setFolderExportFrom(todayDateInputValue());
    onClose();
  }, [onClose]);

  const startPacServer = (id: string) => {
    const pacServer = reusablePacServers.find(
      (item) => item.pacServerId === id
    );
    setPacServerId(id);

    if (pacServer) {
      const fromServer = pacServerFormValuesFromServer(
        pacServer,
        devices.find((item) => item.key === pacServer.deviceKey),
        listenPort
      );
      setValues(
        withReportsOn({
          ...fromServer,
          reports: {
            ...fromServer.reports,
            exportFrom: fromServer.reports.exportFrom || todayDateInputValue(),
          },
        })
      );
    } else if (dicomDevice) {
      const fresh = createPacServerFormValues(dicomDevice, listenPort);
      setValues(
        withReportsOn({
          ...fresh,
          worklist: { ...fresh.worklist, enabled: false },
          exams: { ...fresh.exams, enabled: false },
        })
      );
    }
  };

  const toggleFormat = (format: ReportFormat, checked: boolean) =>
    setFormats((previous) =>
      checked
        ? [...previous.filter((item) => item !== format), format]
        : previous.filter((item) => item !== format)
    );

  const isPacServerValid =
    Boolean(values) &&
    jobs !== null &&
    (Boolean(existingPacServer) ||
      (Boolean(values?.name.trim()) &&
        Boolean(values && buildDicomAddress(values.address))));

  const isFolderValid =
    folderName.trim().length > 0 &&
    trimmedPath.length > 0 &&
    !isDuplicatePath &&
    formats.length > 0 &&
    folderExportFromIso !== null;

  const isStepValid = [
    Boolean(clinicId) &&
      (destination === "ExportFolder" || Boolean(dicomDevice)),
    destination === "PacServer" ? isPacServerValid : isFolderValid,
  ][step];

  const handleNext = () => {
    if (destination === "PacServer" && !values) startPacServer(NEW_PAC_SERVER);
    setStep(1);
  };

  const handleCreate = () => {
    if (!clinicId) return;

    if (destination === "ExportFolder") {
      if (!isFolderValid || !folderExportFromIso) return;

      createConnection(
        {
          clinicId,
          name: folderName.trim(),
          sourceKind: "ExportFolder",
          direction: "Outbound",
          configJson: {
            path: trimmedPath,
            reportTypes: formats,
            exportFrom: folderExportFromIso,
          },
          enabled: true,
          deviceKey: null,
        },
        { onSuccess: handleClose }
      );
      return;
    }

    if (!values || !jobs || !device) return;

    if (existingPacServer) {
      updatePacServer(
        {
          pacServerId: existingPacServer.pacServerId,
          name: existingPacServer.name,
          deviceKey: existingPacServer.deviceKey,
          jobs,
        },
        { onSuccess: handleClose }
      );
      return;
    }

    createPacServer(
      {
        clinicId,
        name: values.name.trim(),
        deviceKey: device.key,
        jobs,
      },
      { onSuccess: handleClose }
    );
  };

  const pacServerOptions = [
    { displayName: t("New DICOM PAC server"), value: NEW_PAC_SERVER },
    ...reusablePacServers.map((pacServer) => ({
      displayName: pacServer.name,
      value: pacServer.pacServerId,
    })),
  ];

  const isPending =
    isCreatingPacServer || isUpdatingPacServer || isCreatingConnection;

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Send RetinaLogik Exams")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            width: "100%",
          }}
        >
          <Button
            sx={ModalCustomCancel}
            onClick={step === 0 ? handleClose : () => setStep(0)}
          >
            {step === 0 ? t("Cancel") : t("Back")}
          </Button>
          {step === 0 ? (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid}
              onClick={handleNext}
            >
              {t("Next")}
            </Button>
          ) : (
            <Button
              sx={ModalCustomConfirm}
              disabled={!isStepValid || isPending}
              onClick={handleCreate}
            >
              {t("Start sending")}
            </Button>
          )}
        </Grid>
      }
    >
      <WizardSteps
        steps={[
          t("Destination"),
          destination === "PacServer" ? t("PAC server") : t("Shared folder"),
        ]}
        current={step}
      />
      <Box display="flex" flexDirection="column" gap="16px" p="16px">
        {step === 0 && (
          <>
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography fontSize="15px" fontWeight={800}>
                {t("Where should finished reports go?")}
              </Typography>
              <ChoiceCard
                name="add-report-destination"
                value="PacServer"
                checked={destination === "PacServer"}
                title={t("PAC server")}
                description={t(
                  "Stored in the clinic's DICOM PAC server, next to the original exam."
                )}
                onSelect={() => setDestination("PacServer")}
              />
              <ChoiceCard
                name="add-report-destination"
                value="ExportFolder"
                checked={destination === "ExportFolder"}
                title={t("Shared folder")}
                description={t(
                  "The hub writes each report into a folder, for an EMR that imports from there."
                )}
                onSelect={() => setDestination("ExportFolder")}
              />
            </Box>
            <ClinicField
              idPrefix="add-report-destination"
              hub={hub}
              clinicId={clinicId}
            />
          </>
        )}

        {step === 1 && destination === "PacServer" && values && (
          <>
            {reusablePacServers.length > 0 && (
              <FormField
                label={t("PAC server")}
                labelId="add-report-destination-pac-server-label"
                required
              >
                <SelectDropdown
                  id="add-report-destination-pac-server-dropdown"
                  labelId="add-report-destination-pac-server-label"
                  dropdownOptions={pacServerOptions}
                  value={
                    pacServerOptions.find(
                      (option) => option.value === pacServerId
                    )?.displayName ?? ""
                  }
                  handleChange={(event) => {
                    const id = pacServerOptions.find(
                      (option) => option.displayName === event.target.value
                    )?.value;
                    if (id !== undefined) startPacServer(id);
                  }}
                />
              </FormField>
            )}
            {existingPacServer ? (
              <Typography fontSize="12px" color={mutedText}>
                {t("Reports go to the address {{name}} already uses.", {
                  name: existingPacServer.name,
                })}
              </Typography>
            ) : (
              <>
                <FormField
                  label={t("Name")}
                  htmlFor="add-report-destination-name-input"
                  required
                >
                  <TextInputField
                    id="add-report-destination-name-input"
                    placeholder={t("e.g. Main PAC server")}
                    value={values.name}
                    maxLength={100}
                    onChange={(event) =>
                      setValues({ ...values, name: event.target.value })
                    }
                  />
                </FormField>
                <AddressFields
                  idPrefix="add-report-destination"
                  value={values.address}
                  disabled={false}
                  remoteAeLabel={t("PAC server AE title")}
                  showHost
                  onChange={(address) =>
                    setValues({
                      ...values,
                      address,
                      reports: { ...values.reports, address },
                    })
                  }
                />
              </>
            )}
            <CutoverDateField
              idPrefix="add-report-destination"
              value={values.reports.exportFrom}
              onChange={(exportFrom) =>
                setValues({
                  ...values,
                  reports: { ...values.reports, exportFrom },
                })
              }
            />
            <Typography fontSize="12px" color={mutedText}>
              {t(
                "Reports of earlier exams are never sent, so the PAC server is not flooded with old reports."
              )}
            </Typography>
          </>
        )}

        {step === 1 && destination === "ExportFolder" && (
          <>
            <FormField
              label={t("Name")}
              htmlFor="add-report-destination-folder-name-input"
              required
            >
              <TextInputField
                id="add-report-destination-folder-name-input"
                placeholder={t("e.g. EMR reports folder")}
                value={folderName}
                maxLength={100}
                onChange={(event) => setFolderName(event.target.value)}
              />
            </FormField>
            <FormField
              label={t("Folder")}
              htmlFor="add-report-destination-folder-path-input"
              required
            >
              <TextInputField
                id="add-report-destination-folder-path-input"
                placeholder="\\emr01\retinalogik-reports"
                value={folderPath}
                maxLength={260}
                error={isDuplicatePath}
                helperText={
                  isDuplicatePath
                    ? t("An EMR connection already exists for this path.")
                    : t(
                        "A local folder on the hub PC or a network share. The hub writes there with its own Windows account."
                      )
                }
                onChange={(event) => setFolderPath(event.target.value)}
              />
            </FormField>
            <Box display="flex" flexDirection="column" gap="4px">
              <Typography fontSize="13px" fontWeight={700}>
                {t("Write reports as")}
              </Typography>
              <Box display="flex" gap="16px" flexWrap="wrap">
                {(["Pdf", "Dicom"] as Array<ReportFormat>).map((format) => (
                  <Box
                    key={format}
                    component="label"
                    htmlFor={`add-report-destination-format-${format}`}
                    display="flex"
                    gap="8px"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                  >
                    <Checkbox
                      id={`add-report-destination-format-${format}`}
                      checked={formats.includes(format)}
                      onChange={(event) =>
                        toggleFormat(format, event.target.checked)
                      }
                      sx={{
                        padding: "2px",
                        color: onSurface,
                        "&.Mui-checked": { color: ACCENT_TEAL },
                      }}
                    />
                    <Typography fontSize="13px" color={onSurface}>
                      {format === "Pdf"
                        ? t("PDF, one file per report")
                        : t("DICOM, one file per report section")}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {formats.length === 0 && (
                <Typography fontSize="12px" color={mutedText}>
                  {t("Pick at least one format.")}
                </Typography>
              )}
            </Box>
            <CutoverDateField
              idPrefix="add-report-destination-folder"
              value={folderExportFrom}
              onChange={setFolderExportFrom}
            />
          </>
        )}
      </Box>
    </DefaultModal>
  );
};

export default AddReportDestinationModal;
